import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Business  from './pages/Business';
import Products   from './pages/Products';
import Categories    from './pages/Categories';
import Applications  from './pages/Applications';
import Clients       from './pages/Clients';
import Vendors         from './pages/Vendors';
import PurchaseOrders  from './pages/PurchaseOrders';
import InventoryCategory from './pages/InventoryCategory';
import Package           from './pages/Package';
import ProductMapping    from './pages/ProductMapping';
import Dealers          from './pages/Dealers';
import DealerDetail     from './pages/DealerDetail';
import CreditList        from './pages/CreditList';
import RenewalList       from './pages/RenewalList';
import SalesHistory      from './pages/SalesHistory';
import RenewalHistory       from './pages/RenewalHistory';
import ReplacementHistory  from './pages/ReplacementHistory';
import InstallerDashboard from './pages/InstallerDashboard';
import GetDevice          from './pages/GetDevice';
import InstallerMapping   from './pages/InstallerMapping';
import DealerDashboard    from './pages/DealerDashboard';
import DealerClients      from './pages/DealerClients';
import DealerInventory    from './pages/DealerInventory';
import DealerWallet       from './pages/DealerWallet';
import DealerMapping      from './pages/DealerMapping';
import Service            from './pages/Service';
import Complaint          from './pages/Complaint';

// Per-role route allow-lists. An entry ending in '/' is a prefix match (for
// dynamic routes like /inventory/:id); others match the pathname exactly.
// Roles not listed here (admin, manager) can reach every admin route.
const ROLE_ROUTES = {
  accountant: [
    '/dashboard', '/profile/clients', '/purchase-orders', '/credit-list',
    '/sales-history', '/renewal-history', '/replacement-history',
  ],
  sales_support: [
    '/dashboard', '/profile/clients', '/inventory/', '/packages',
    '/product-mapping', '/renewal-list', '/sales-history', '/renewal-history',
    '/replacement-history', '/service', '/complaint',
  ],
};

function routeAllowed(list, pathname) {
  return list.some(p => (p.endsWith('/') ? pathname.startsWith(p) : pathname === p));
}

// Admin-only guard
function Guard({ children }) {
  const { token, user } = useAuth();
  const location = useLocation();
  if (!token) return <Navigate to="/login" replace />;
  if (user && user.role === 'installer') return <Navigate to="/installer/dashboard" replace />;
  if (user && user.role === 'dealer')    return <Navigate to="/dealer/dashboard"    replace />;
  const allow = user && ROLE_ROUTES[user.role];
  if (allow && !routeAllowed(allow, location.pathname))
    return <Navigate to="/dashboard" replace />;
  return children;
}

// Installer guard (installers + admins allowed)
function InstallerGuard({ children }) {
  const { token } = useAuth();
  return token ? children : <Navigate to="/login" replace />;
}

// Dealer guard
function DealerGuard({ children }) {
  const { token, user } = useAuth();
  if (!token) return <Navigate to="/login" replace />;
  if (user && user.role !== 'dealer') return <Navigate to="/dashboard" replace />;
  return children;
}

// Shared guard — admin + dealer both allowed
function SharedGuard({ children }) {
  const { token } = useAuth();
  return token ? children : <Navigate to="/login" replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login"            element={<Login />} />

          {/* Admin routes */}
          <Route path="/dashboard"        element={<Guard><Dashboard /></Guard>} />
          <Route path="/users"            element={<Guard><Users /></Guard>} />
          <Route path="/profile/business" element={<Guard><Business /></Guard>} />
          <Route path="/profile/products"    element={<Guard><Products /></Guard>} />
          <Route path="/profile/categories"   element={<Guard><Categories /></Guard>} />
          <Route path="/profile/applications" element={<Guard><Applications /></Guard>} />
          <Route path="/profile/clients"      element={<Guard><Clients /></Guard>} />
          <Route path="/profile/vendors"      element={<Guard><Vendors /></Guard>} />
          <Route path="/purchase-orders"      element={<Guard><PurchaseOrders /></Guard>} />
          <Route path="/inventory/:categoryId" element={<Guard><InventoryCategory /></Guard>} />
          <Route path="/packages"             element={<Guard><Package /></Guard>} />
          <Route path="/product-mapping"     element={<Guard><ProductMapping /></Guard>} />
          <Route path="/dealers"             element={<Guard><Dealers /></Guard>} />
          <Route path="/dealers/:id"         element={<Guard><DealerDetail /></Guard>} />
          <Route path="/credit-list"         element={<Guard><CreditList /></Guard>} />
          <Route path="/renewal-list"        element={<Guard><RenewalList /></Guard>} />
          <Route path="/sales-history"       element={<Guard><SalesHistory /></Guard>} />
          <Route path="/renewal-history"      element={<Guard><RenewalHistory /></Guard>} />
          <Route path="/replacement-history" element={<Guard><ReplacementHistory /></Guard>} />
          <Route path="/service"            element={<Guard><Service /></Guard>} />
          <Route path="/complaint"          element={<Guard><Complaint /></Guard>} />

          {/* Installer routes */}
          <Route path="/installer/dashboard"  element={<InstallerGuard><InstallerDashboard /></InstallerGuard>} />
          <Route path="/installer/get-device" element={<InstallerGuard><GetDevice /></InstallerGuard>} />
          <Route path="/installer/mapping"    element={<InstallerGuard><InstallerMapping /></InstallerGuard>} />

          {/* Dealer routes */}
          <Route path="/dealer/dashboard"          element={<DealerGuard><DealerDashboard /></DealerGuard>} />
          <Route path="/dealer/inventory"          element={<DealerGuard><DealerInventory /></DealerGuard>} />
          <Route path="/dealer/wallet"             element={<DealerGuard><DealerWallet /></DealerGuard>} />
          <Route path="/dealer/clients"            element={<DealerGuard><DealerClients /></DealerGuard>} />
          <Route path="/dealer/credit-list"        element={<DealerGuard><CreditList /></DealerGuard>} />
          <Route path="/dealer/renewal-list"       element={<DealerGuard><RenewalList /></DealerGuard>} />
          <Route path="/dealer/sales-history"      element={<DealerGuard><SalesHistory /></DealerGuard>} />
          <Route path="/dealer/renewal-history"    element={<DealerGuard><RenewalHistory /></DealerGuard>} />
          <Route path="/dealer/replacement-history" element={<DealerGuard><ReplacementHistory /></DealerGuard>} />

          {/* Shared — admin + dealer */}
          <Route path="/dealer-mapping"   element={<SharedGuard><DealerMapping /></SharedGuard>} />

          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
